/**
 * QChatInput Component (v5.1 - Mobile Font Fix)
 * Clean, minimal chat input with professional styling
 *
 * Key Improvements v5.1 (Mobile Font Fix):
 * - 18px font on mobile (prevents iOS auto-zoom on focus)
 * - 52px min-height on mobile for better tap target
 * - Responsive padding for comfortable mobile typing
 *
 * v5.0 (Inline Send Button):
 * - Send button INSIDE input field (like ChatGPT/Claude)
 * - Eliminates 70px right padding hack
 * - Full-width input on all screen sizes
 * - No text overlap with send button
 *
 * v4.0 (Feature Flagged):
 * - Expandable input grows UPWARD as user types
 * - Max 6 lines on desktop, 4 on mobile
 * - New placeholder: "Ask Q..."
 * - Feature flag: EXPANDABLE_INPUT for safe rollback
 *
 * v3.0:
 * - Bolt.new inspired design
 * - Pure dark background (#0A0A0F)
 * - Crisp placeholder text
 * - Minimal styling, professional look
 */

import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Box, TextField, IconButton, useMediaQuery, useTheme } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import { FEATURE_FLAGS } from '../../config/featureFlags';

// ═══════════════════════════════════════════════════════════════════════════
// INPUT CONFIGURATION (Feature Flagged)
// ═══════════════════════════════════════════════════════════════════════════

const INPUT_CONFIG = {
  // Premium (new) - expandable input
  premium: {
    placeholder: 'Ask Q...',
    minRows: 1,
    maxRowsDesktop: 6,
    maxRowsMobile: 4,
  },
  // Original (fallback)
  original: {
    placeholder: 'Message Q...',
    minRows: 1,
    maxRowsDesktop: 3,
    maxRowsMobile: 3,
  }
};

const getInputConfig = () => {
  return FEATURE_FLAGS.EXPANDABLE_INPUT ? INPUT_CONFIG.premium : INPUT_CONFIG.original;
};

/**
 * QChatInput Component
 * @param {function} onSend - Callback when message is sent
 * @param {function} onCancel - Callback to cancel streaming
 * @param {boolean} isLoading - Whether AI is responding
 * @param {boolean} disabled - Whether input is disabled
 * @param {boolean} expanded - Whether input is in expanded mode
 * @param {string} placeholder - Input placeholder text (overrides config)
 */
const QChatInput = forwardRef(({
  onSend,
  onCancel,
  isLoading = false,
  disabled = false,
  expanded = false,
  placeholder // Now optional, uses config default
}, ref) => {
  const [message, setMessage] = useState('');
  const inputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Get config based on feature flag
  const config = getInputConfig();
  const effectivePlaceholder = placeholder || config.placeholder;
  const maxRows = isMobile ? config.maxRowsMobile : config.maxRowsDesktop;

  // Expose focus method to parent
  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus()
  }));

  // Focus input on mount and when not loading
  useEffect(() => {
    if (inputRef.current && !isLoading) {
      inputRef.current.focus();
    }
  }, [isLoading]);

  const handleSend = () => {
    if (message.trim() && !isLoading && !disabled) {
      onSend(message.trim());
      setMessage('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const hasContent = message.trim().length > 0;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        transition: 'all 0.12s ease'
      }}
    >
      {/* Text Input - Full width with inline send button */}
      <TextField
        inputRef={inputRef}
        fullWidth
        multiline
        minRows={expanded ? 3 : config.minRows}
        maxRows={expanded ? maxRows : maxRows}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={effectivePlaceholder}
        disabled={disabled}
        sx={(theme) => ({
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(24, 24, 27, 0.6)',
            borderRadius: '12px',
            color: '#F5F5F5',
            // MOBILE FIX: 18px on mobile prevents iOS auto-zoom on focus
            fontSize: '18px !important',
            lineHeight: 1.5,
            minHeight: '52px',
            // v5.0: Right padding for inline button (44px button + 8px gap)
            paddingRight: '52px',
            // Desktop overrides
            [theme.breakpoints.up('sm')]: {
              fontSize: '1rem !important', // 16px on desktop
              minHeight: '48px'
            },
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.10)',
              borderWidth: '1px'
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.15)'
            },
            '&.Mui-focused fieldset': {
              borderColor: 'rgba(39, 181, 109, 0.4)',
              borderWidth: '1.5px'
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(24, 24, 27, 0.3)',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.03)'
              }
            }
          },
          // Target the actual textarea element directly
          '& .MuiOutlinedInput-input, & textarea': {
            // MOBILE FIX: Larger padding and font on mobile
            padding: '14px 16px',
            fontSize: '18px !important', // 18px on mobile - prevents iOS zoom
            color: '#F5F5F5 !important',
            // Desktop overrides
            [theme.breakpoints.up('sm')]: {
              padding: '12px 14px',
              fontSize: '1rem !important' // 16px on desktop
            },
            '&::placeholder': {
              color: '#71717A !important', // Brighter placeholder for better visibility
              opacity: '1 !important',
              fontSize: 'inherit'
            }
          },
          // Also target inputBase for good measure
          '& .MuiInputBase-input': {
            fontSize: '18px !important',
            [theme.breakpoints.up('sm')]: {
              fontSize: '1rem !important'
            }
          }
        })}
      />

      {/* v5.0: Send/Cancel Button - INSIDE input field (bottom-right) */}
      <Box
        sx={{
          position: 'absolute',
          right: '6px',
          bottom: '6px',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        {isLoading ? (
          <IconButton
            onClick={handleCancel}
            size="small"
            sx={{
              backgroundColor: 'rgba(239, 68, 68, 0.15)',
              color: '#EF4444',
              width: 32,
              height: 32,
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.25)'
              }
            }}
          >
            <StopIcon sx={{ fontSize: '0.875rem' }} />
          </IconButton>
        ) : (
          <IconButton
            onClick={handleSend}
            disabled={!hasContent || disabled}
            size="small"
            sx={{
              backgroundColor: hasContent && !disabled
                ? '#27B56D'
                : 'transparent',
              color: hasContent && !disabled
                ? '#FFFFFF'
                : '#52525B',
              width: 32,
              height: 32,
              borderRadius: '8px',
              border: 'none',
              transition: 'all 0.15s ease',
              '&:hover': {
                backgroundColor: hasContent && !disabled
                  ? '#22A55E'
                  : 'rgba(255, 255, 255, 0.05)'
              },
              '&.Mui-disabled': {
                backgroundColor: 'transparent',
                color: '#3F3F46'
              }
            }}
          >
            <SendIcon sx={{ fontSize: '0.875rem' }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
});

QChatInput.displayName = 'QChatInput';

export default QChatInput;
